/**
 * Video Player Component
 *
 * A responsive video player that displays a single video (via Vimeo) with optional
 * playlist navigation. Supports both desktop (side-by-side layout) and mobile (drawer)
 * playlist views.
 *
 * Features:
 * - Fetch video metadata and external Vimeo URL by videoId
 * - Auto-advance to next video in playlist when current video ends (5s delay)
 * - Expandable accordion for full/truncated video descriptions
 * - Responsive: desktop shows playlist on right; mobile shows in off-canvas drawer
 * - Error and loading state handling with user feedback
 * - Configurable autoplay via prop (defaults to true if not specified)
 *
 * @component
 * @param {string} videoId - ID of the video to load initially
 * @param {Function} onClose - Callback fired when user clicks close button
 * @param {Array<Object>} [playlist] - Optional array of videos for playlist navigation
 *   Each item should have: { id, externalId, title, ... }
 * @param {boolean} [autoplay] - Whether to autoplay video on load (defaults to true)
 */
import React, { useState, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import Player from "@vimeo/player";
import {
  Typography,
  IconButton,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMediaQuery } from "react-responsive";
import { schedulerApi } from "../services/api";

const VideoComponent = ({
  videoId,
  onClose,
  playlist,
  autoplay,
  isPhysioSource,
}) => {
  // Full video metadata object fetched from API
  const [videoData, setVideoData] = useState(null);
  // Vimeo embed URL derived from videoData.externalId (format: https://player.vimeo.com/video/{externalId})
  const [currentVideoURL, setCurrentVideoURL] = useState("");
  // Current index in the playlist array (used for next/previous navigation)
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  // ReactPlayer playing state (muted on desktop, autoplay controlled by prop)
  const [playing, setPlaying] = useState(true);
  // Mobile drawer state for playlist visibility
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Accordion expanded state for video description (true = show full, false = show truncated)
  const [isExpanded, setIsExpanded] = useState(false);
  // Loading indicator while fetching video data from API
  const [loading, setLoading] = useState(false);
  // Error message displayed if video fetch fails
  const [error, setError] = useState(null);

  const iframeRef = useRef(null);

  // Device detection: true if viewport width ≤ 767px
  const isMobile = useMediaQuery({ maxWidth: 767 });

  /**
   * Fetch video metadata by ID and set up the Vimeo player URL
   * @param {string} id - Video ID to fetch
   */
  const fetchVideoData = async (id, isPhysioSource) => {
    setLoading(true);
    setError(null);

    try {
      const data = await schedulerApi.getVideoById(id, isPhysioSource);
      setVideoData(data);
      setCurrentVideoURL(data.url);
    } catch (error) {
      console.error("Error fetching video data:", error);
      setError(error.message || "Failed to load video");
    } finally {
      setLoading(false);
    }
  };

  // Load initial video when videoId prop changes
  
  useEffect(() => {
    if (videoId) {
      fetchVideoData(videoId, isPhysioSource);
    }
  }, [videoId, isPhysioSource]);

  useEffect(() => {
    if (iframeRef.current && currentVideoURL) {
      const player = new Player(iframeRef.current);

      player
        .ready()
        .then(() => {
          player.setVolume(0); // Mute
          player.play(); // Force autoplay
        })
        .catch((error) => {
          console.error("Vimeo player error:", error);
        });
    }
  }, [currentVideoURL]);

  /**
   * Handle playlist item click: update current index, fetch video, and close mobile drawer
   * @param {number} index - Index in playlist array
   */
  const handlePlaylistClick = (index) => {
    setCurrentVideoIndex(index);
    fetchVideoData(playlist[index].externalId, isPhysioSource);
    setPlaying(true);
    if (isMobile) {
      setIsDrawerOpen(false);
    }
  };

  /**
   * Auto-advance to next video when current video ends
   * If at end of playlist, stops playback. Otherwise, waits 5 seconds and loads next video.
   */
  const handleVideoEnd = () => {
    if (playlist && currentVideoIndex < playlist.length - 1) {
      setTimeout(() => {
        const nextVideoIndex = currentVideoIndex + 1;
        handlePlaylistClick(nextVideoIndex);
      }, 5000);
    } else {
      setPlaying(false);
    }
  };

  /**
   * Toggle mobile playlist drawer visibility
   */
  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  /**
   * Truncate description text to a given word count and append "..."
   * Used to show preview text in collapsed accordion state
   * @param {string} text - Full description text
   * @param {number} wordCount - Number of words to keep
   * @returns {string} Truncated text with "..." appended
   */
  const getShortDescription = (text, wordCount) => {
    if (!text) return "";
    return text.split(" ").slice(0, wordCount).join(" ") + "...";
  };

  /**
   * Handle accordion expand/collapse state change
   * @param {Event} event - Accordion change event
   * @param {boolean} expanded - True if accordion should be expanded
   */
  const handleAccordionChange = (event, expanded) => {
    setIsExpanded(expanded);
  };

  if (loading) {
    return (
      <div
        className="video-container"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "400px",
          color: "white",
        }}
      >
        <Typography variant="h6">Loading video...</Typography>
        <IconButton
          onClick={onClose}
          style={{
            color: "white",
            position: "absolute",
            right: isMobile ? "10px" : "-50px",
            top: 0,
          }}
        >
          <CloseIcon style={{ width: 36, height: 36 }} />
        </IconButton>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="video-container"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "400px",
          color: "white",
        }}
      >
        <Typography variant="h6" color="error">
          Error loading video
        </Typography>
        <Typography variant="body2" style={{ marginTop: "8px" }}>
          {error?.message}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{
            color: "white",
            position: "absolute",
            right: isMobile ? "10px" : "-50px",
            top: 0,
          }}
        >
          <CloseIcon style={{ width: 36, height: 36 }} />
        </IconButton>
      </div>
    );
  }

  return (
    <div
      className="video-container"
      style={{
        position: "relative",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        height: "100%",
      }}
    >
      {isMobile && (
        <IconButton
          onClick={toggleDrawer}
          style={{
            color: "white",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 100,
          }}
        >
          <MenuIcon style={{ width: 36, height: 36 }} />
        </IconButton>
      )}
      <div
        className="video-player-wrapper"
        style={{
          flex: 3,
          marginRight: isMobile ? "0px" : "20px",
          width: "100%",
        }}
      >
        <div className="video-player">
          {currentVideoURL &&
            videoData &&
            (isPhysioSource ? (
              <iframe
                ref={iframeRef}
                src={currentVideoURL}
                width="100%"
                height={isMobile ? "300px" : "500px"}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={videoData.title}
              />
            ) : (
              <ReactPlayer
                url={currentVideoURL}
                controls={true}
                width="100%"
                height={isMobile ? "300px" : "500px"}
                onEnded={handleVideoEnd}
                playing={autoplay ?? playing}
                muted={true}
                playsinline
                key={currentVideoURL}
              />
            ))}
        </div>
        <div
          className="video-details"
          style={{ padding: isMobile ? "10px" : "20px" }}
        >
          {videoData && (
            <>
              <Typography variant="caption" style={{ color: "white" }}>
                {videoData.preferences
                  ?.filter((pref) => pref.name !== "Random")
                  .map((pref) => pref.name)
                  .join(", ")}
                {/* Display exercise preferences (tags) */}
              </Typography>
              <Typography variant="h6" style={{ color: "white" }}>
                {videoData.title}
              </Typography>

              {/* Expandable description: shows preview when collapsed, full text when expanded */}
              <Accordion
                style={{ backgroundColor: "transparent", color: "white" }}
                elevation={0}
                expanded={isExpanded}
                onChange={handleAccordionChange}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon style={{ color: "white" }} />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  {!isExpanded && (
                    <Typography variant="body2">
                      {getShortDescription(videoData.description, 12)}
                    </Typography>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" style={{ color: "white" }}>
                    {videoData.description}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </div>

        {/* Close button: positioned absolutely, right edge on mobile, outside right on desktop */}
        <IconButton
          onClick={onClose}
          style={{
            color: "white",
            position: "absolute",
            right: isMobile ? "10px" : "-50px",
            top: 0,
          }}
        >
          <CloseIcon style={{ width: 36, height: 36 }} />
        </IconButton>
      </div>
      {playlist && playlist.length > 0 && (
        <>
          {!isMobile ? (
            <div
              className="playlist"
              style={{ width: "200px", height: "600px", overflowY: "auto" }}
            >
              {playlist.map((video, index) => (
                <div
                  key={video.id}
                  style={{ marginBottom: "10px", cursor: "pointer" }}
                  onClick={() => handlePlaylistClick(index)}
                >
                  <img
                    src={`${process.env.REACT_APP_FIREBASE_API_HOST}/api/thumbnails/${video.externalId}`}
                    alt={video.title}
                    style={{ width: "100%", height: "auto" }}
                  />
                  <Typography variant="subtitle2" style={{ color: "white" }}>
                    {video.title}
                  </Typography>
                </div>
              ))}
            </div>
          ) : (
            <Drawer
              anchor="right"
              open={isDrawerOpen}
              onClose={toggleDrawer}
              PaperProps={{
                style: { backgroundColor: "#00023a", width: "80%" },
              }}
            >
              <div style={{ padding: "10px" }}>
                <IconButton
                  onClick={toggleDrawer}
                  style={{ color: "white", alignSelf: "flex-end" }}
                >
                  <CloseIcon />
                </IconButton>
                {playlist.map((video, index) => (
                  <div
                    key={video.id}
                    style={{ marginBottom: "10px", cursor: "pointer" }}
                    onClick={() => handlePlaylistClick(index)}
                  >
                    <img
                      src={`${process.env.REACT_APP_FIREBASE_API_HOST}/api/thumbnails/${video.externalId}`}
                      alt={video.title}
                      style={{ width: "100%", height: "auto" }}
                    />
                    <Typography variant="subtitle2" style={{ color: "white" }}>
                      {video.title}
                    </Typography>
                  </div>
                ))}
              </div>
            </Drawer>
          )}
        </>
      )}
    </div>
  );
};

export default VideoComponent;
